import { Link } from 'react-router';
import { useTranslation } from 'react-i18next';
import type { AdminDashboard as Dashboard, FunnelStep } from '@gg/shared';
import { Card, ProgressBar, Stat, Table, Td, Th } from '@/components/ui';
import { formatDateTime, formatNumber } from '@/lib/dates';
import i18n, { formatPrice } from '@/lib/i18n';
import { useAdminDashboard } from '@/lib/queries';

const PERIODS = ['today', 'last7Days', 'sinceOpening'] as const;

/** "62 %" of a step against the one before it. */
const rate = (part: number, whole: number) =>
  new Intl.NumberFormat(i18n.language, { style: 'percent' }).format(part / whole);

/**
 * A-09: the back-office landing page. Read-only figures from the database: places per format,
 * revenue, the order funnel, documents waiting for review and Brevo jobs that failed.
 */
export function AdminDashboard() {
  const { t } = useTranslation();
  const dash = useAdminDashboard();

  return (
    <section className="space-y-8">
      <div className="flex items-end justify-between gap-4">
        <h1 className="font-display text-section-lg font-bold">{t('admin.dashboard.title')}</h1>
        {dash.data && (
          <span className="text-sm text-ink-muted">
            {t('admin.dashboard.updated', { date: formatDateTime(dash.data.generatedAt) })}
          </span>
        )}
      </div>
      {dash.isLoading && <p className="text-center text-ink-muted">…</p>}
      {dash.isError && (
        <p role="alert" className="text-sm text-danger">
          {t('admin.dashboard.error')}
        </p>
      )}
      {dash.data && <Figures d={dash.data} />}
    </section>
  );
}

function Figures({ d }: { d: Dashboard }) {
  const { t } = useTranslation();
  const { revenue, brevo } = d;
  return (
    <>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Stat
          label={t('admin.dashboard.paidPlaces')}
          value={formatNumber(d.total.paid)}
          hint={
            d.total.capacity === null
              ? t('admin.dashboard.heldHint', { count: d.total.held })
              : t('admin.dashboard.capacityHint', {
                  capacity: formatNumber(d.total.capacity),
                  count: d.total.held,
                })
          }
        />
        <Stat
          label={t('admin.dashboard.net')}
          value={formatPrice(revenue.netCents)}
          hint={t('admin.dashboard.netHint', { count: revenue.paidOrders })}
        />
        <Stat
          label={t('admin.dashboard.documentsPending')}
          value={d.documentsPending}
          hint={
            // The A-05 review queue: the tile counts its items (#48).
            d.documentsPending > 0 && (
              <Link to="/admin/documents">{t('admin.dashboard.documentsPendingLink')}</Link>
            )
          }
        />
        <Stat
          label={t('admin.dashboard.brevoFailed')}
          value={brevo.failed}
          tone={brevo.failed > 0 ? 'danger' : 'ink'}
          hint={t('admin.dashboard.brevoStuck', { count: brevo.stuck })}
        />
      </div>

      <Card className="gap-4!">
        <h2 className="text-lg font-bold">{t('admin.dashboard.places')}</h2>
        <Table>
          <thead>
            <tr>
              <Th>{t('admin.dashboard.col.format')}</Th>
              <Th className="w-1/3" />
              <Th className="text-right">{t('admin.dashboard.col.paid')}</Th>
              <Th className="text-right">{t('admin.dashboard.col.held')}</Th>
              <Th className="text-right">{t('admin.dashboard.col.capacity')}</Th>
              <Th className="text-right">{t('admin.dashboard.col.remaining')}</Th>
            </tr>
          </thead>
          <tbody>
            {[
              ...d.formats.map((f) => ({ ...f, total: false })),
              { ...d.total, id: 'total', name: t('admin.dashboard.total'), total: true },
            ].map((f) => (
              <tr key={f.id}>
                <Td className={f.total ? 'font-bold' : 'font-semibold'}>{f.name}</Td>
                <Td>
                  {f.capacity !== null && f.capacity > 0 && (
                    <ProgressBar
                      value={(f.paid + f.held) / f.capacity}
                      label={t('admin.dashboard.fill', { name: f.name })}
                    />
                  )}
                </Td>
                <Td className="text-right tabular-nums">{formatNumber(f.paid)}</Td>
                <Td className="text-right tabular-nums text-ink-muted">{formatNumber(f.held)}</Td>
                <Td className="text-right tabular-nums">
                  {f.capacity === null ? t('admin.dashboard.unlimited') : formatNumber(f.capacity)}
                </Td>
                <Td className="text-right tabular-nums">
                  {f.remaining === null ? t('admin.none') : formatNumber(f.remaining)}
                </Td>
              </tr>
            ))}
          </tbody>
        </Table>
        <p className="m-0 text-helper text-ink-muted">{t('admin.dashboard.placesHint')}</p>
      </Card>

      <div className="grid gap-6 lg:grid-cols-2">
        <Card className="gap-4!">
          <h2 className="text-lg font-bold">{t('admin.dashboard.revenue')}</h2>
          <Table>
            <tbody>
              <MoneyRow label={t('admin.dashboard.gross')} cents={revenue.grossCents} />
              <MoneyRow
                label={t('admin.dashboard.insurance', { count: revenue.insuredOrders })}
                cents={revenue.insuranceCents}
                muted
              />
              <MoneyRow label={t('admin.dashboard.refunded')} cents={-revenue.refundedCents} />
              <MoneyRow label={t('admin.dashboard.net')} cents={revenue.netCents} strong />
              <MoneyRow
                label={t('admin.dashboard.discounts')}
                cents={revenue.discountCents}
                muted
              />
            </tbody>
          </Table>
          <p className="m-0 text-helper text-ink-muted">{t('admin.dashboard.revenueHint')}</p>
        </Card>

        <Card className="gap-4!">
          <h2 className="text-lg font-bold">{t('admin.dashboard.funnel')}</h2>
          <Table>
            <thead>
              <tr>
                <Th />
                {PERIODS.map((p) => (
                  <Th key={p} className="text-right">
                    {t(`admin.dashboard.period.${p}`)}
                  </Th>
                ))}
              </tr>
            </thead>
            <tbody>
              {(['started', 'paid', 'refunded', 'vouchersUsed'] as const).map((step) => (
                <tr key={step}>
                  <Td className="font-semibold">{t(`admin.dashboard.step.${step}`)}</Td>
                  {PERIODS.map((p) => (
                    <FunnelCell key={p} s={d.funnel[p]} step={step} />
                  ))}
                </tr>
              ))}
            </tbody>
          </Table>
          <p className="m-0 text-helper text-ink-muted">{t('admin.dashboard.funnelHint')}</p>
        </Card>
      </div>

      <Card className="gap-4!">
        <h2 className="text-lg font-bold">{t('admin.dashboard.brevo')}</h2>
        <p className="m-0 text-sm text-ink-muted">{t('admin.dashboard.brevoIntro')}</p>
        {brevo.latestFailures.length === 0 ? (
          <p className="m-0 text-sm text-ink-muted">{t('admin.dashboard.brevoNone')}</p>
        ) : (
          <Table>
            <thead>
              <tr>
                <Th>{t('admin.dashboard.col.date')}</Th>
                <Th>{t('admin.dashboard.col.type')}</Th>
                <Th>{t('admin.dashboard.col.email')}</Th>
                <Th className="text-right">{t('admin.dashboard.col.attempts')}</Th>
                <Th>{t('admin.dashboard.col.error')}</Th>
              </tr>
            </thead>
            <tbody>
              {brevo.latestFailures.map((j) => (
                <tr key={j.id}>
                  <Td className="whitespace-nowrap text-ink-muted">
                    {formatDateTime(j.createdAt)}
                  </Td>
                  <Td className="font-mono text-sm">{j.type}</Td>
                  <Td>{j.email ?? t('admin.none')}</Td>
                  <Td className="text-right tabular-nums">{j.attempts}</Td>
                  <Td className="max-w-md break-words text-sm text-ink-muted">
                    {j.lastError ?? t('admin.none')}
                  </Td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card>
    </>
  );
}

function MoneyRow({
  label,
  cents,
  strong,
  muted,
}: {
  label: string;
  cents: number;
  strong?: boolean;
  muted?: boolean;
}) {
  const tone = strong ? 'font-bold' : muted ? 'text-ink-muted' : '';
  return (
    <tr>
      <Td className={tone}>{label}</Td>
      <Td className={`text-right tabular-nums ${tone}`}>{formatPrice(cents)}</Td>
    </tr>
  );
}

function FunnelCell({ s, step }: { s: FunnelStep; step: keyof FunnelStep }) {
  // Paid against started, refunded and vouchers against paid.
  const base = step === 'paid' ? s.started : step === 'started' ? 0 : s.paid;
  return (
    <Td className="text-right tabular-nums">
      {formatNumber(s[step])}
      {base > 0 && <span className="block text-helper text-ink-muted">{rate(s[step], base)}</span>}
    </Td>
  );
}
